import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { TOPICS, TOPIC_LABEL, type Topic } from "@/lib/topics";
import { checkAdminUnlocked } from "@/lib/admin-gate.functions";
import {
  fetchCuratorBatch,
  fetchCuratorSummary,
  saveCuratorVerdicts,
  type CuratorCandidate,
} from "@/lib/curator-lab.functions";

export const Route = createFileRoute("/admin_/kurator")({
  head: () => ({
    meta: [
      { title: "Kurator-lab – Flâneriet" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  loader: async () => {
    const { unlocked } = await checkAdminUnlocked();
    if (!unlocked) throw redirect({ to: "/admin/login" });
    return null;
  },
  component: KuratorLab,
});

type Verdict = "gold" | "anti" | "skip";

function pickDefaultTopics(byTopic: Array<{ topic: Topic; gold: number }>): Topic[] {
  // Vælg de 5 emner der har færrest guld-eksempler – dem AI'en har mindst kalibrering på.
  return [...byTopic]
    .sort((a, b) => a.gold - b.gold)
    .slice(0, 5)
    .map((t) => t.topic);
}

function KuratorLab() {
  const fetchBatch = useServerFn(fetchCuratorBatch);
  const saveVerdicts = useServerFn(saveCuratorVerdicts);

  const summary = useQuery({
    queryKey: ["curator-summary"],
    queryFn: () => fetchCuratorSummary(),
  });

  const defaults = useMemo<Topic[]>(() => {
    if (summary.data) return pickDefaultTopics(summary.data.byTopic);
    return ["vin", "kunst", "musik", "mad", "levekunst"] as Topic[];
  }, [summary.data]);

  const [selected, setSelected] = useState<Topic[]>(defaults);
  useMemo(() => setSelected(defaults), [defaults]);

  const [candidates, setCandidates] = useState<CuratorCandidate[]>([]);
  const [verdicts, setVerdicts] = useState<Record<string, Verdict>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  function setTopicAt(idx: number, t: Topic) {
    setSelected((prev) => {
      const next = [...prev];
      next[idx] = t;
      return next;
    });
  }

  async function onFetch() {
    setLoading(true);
    setStatus(null);
    setErrors([]);
    try {
      // Dedup topics — samme emne to gange giver ingen mening
      const uniq = Array.from(new Set(selected)).slice(0, 5);
      const res = await fetchBatch({ data: { topics: uniq, perTopic: 5 } });
      setCandidates(res.candidates);
      setVerdicts({});
      setReasons({});
      setErrors(res.errors);
      setStatus(`Hentede ${res.candidates.length} kandidater · ~${uniq.length * 5} Firecrawl-units brugt.`);
    } catch (e) {
      setStatus(`Kunne ikke hente: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  async function onSave() {
    const items = candidates
      .map((c) => ({
        candidate: c,
        v: verdicts[c.url],
      }))
      .filter((x) => x.v === "gold" || x.v === "anti")
      .map(({ candidate, v }) => ({
        topic: candidate.topic,
        verdict: v as "gold" | "anti",
        url: candidate.url,
        title: candidate.title,
        source: candidate.source,
        excerpt: candidate.excerpt,
        reason: reasons[candidate.url]?.trim() || null,
      }));
    if (items.length === 0) {
      setStatus("Marker mindst én kandidat som guld eller afvis før du gemmer.");
      return;
    }
    setSaving(true);
    try {
      const res = await saveVerdicts({ data: { items } });
      setStatus(`Gemt ${res.saved} valg (${res.gold} guld · ${res.anti} anti). AI-dommeren læser dem fra næste kørsel.`);
      // Ryd de kort vi har taget stilling til, så det er tydeligt hvad der er tilbage
      setCandidates((prev) => prev.filter((c) => !verdicts[c.url] || verdicts[c.url] === "skip"));
      setVerdicts({});
      setReasons({});
      await summary.refetch();
    } catch (e) {
      setStatus(`Kunne ikke gemme: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  const byTopic = candidates.reduce<Record<Topic, CuratorCandidate[]>>((acc, c) => {
    (acc[c.topic] ||= []).push(c);
    return acc;
  }, {} as Record<Topic, CuratorCandidate[]>);

  const goldCount = Object.values(verdicts).filter((v) => v === "gold").length;
  const antiCount = Object.values(verdicts).filter((v) => v === "anti").length;

  return (
    <div className="mx-auto max-w-6xl px-6 py-12">
      <header className="border-b border-rule pb-6 flex items-start justify-between gap-6">
        <div>
          <p className="eyebrow">Redaktion · kalibrering</p>
          <h1 className="mt-2 font-serif text-4xl">Kurator-lab</h1>
          <p className="mt-2 meta italic max-w-2xl">
            Vælg 5 emner, hent 5 kandidater pr. emne, og peg på hvilke der er guld og hvilke der er afvist.
            Dine valg bliver referencer AI-dommeren læser i sin prompt – så morgenkørslen efterligner din smag.
          </p>
        </div>
        <Link to="/admin" className="eyebrow text-ink-soft hover:text-bordeaux shrink-0">tilbage</Link>
      </header>

      <section className="py-6 border-b border-rule">
        <p className="eyebrow mb-3">Referencer AI'en allerede har</p>
        {summary.data ? (
          <p className="font-serif text-[15px] text-ink-body">
            <strong>{summary.data.goldTotal}</strong> guld ·{" "}
            <strong>{summary.data.antiTotal}</strong> anti ·{" "}
            <strong>{summary.data.topicsCovered}</strong> af {TOPICS.length} emner har guld-eksempler.
          </p>
        ) : (
          <p className="meta italic">Henter…</p>
        )}
      </section>

      <section className="py-6 border-b border-rule">
        <p className="eyebrow mb-3">Vælg 5 emner (default = de 5 med færrest guld-eksempler)</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[0, 1, 2, 3, 4].map((i) => (
            <select
              key={i}
              value={selected[i] ?? ""}
              onChange={(e) => setTopicAt(i, e.target.value as Topic)}
              className="border border-rule bg-paper px-3 py-2 font-serif text-[15px]"
            >
              {TOPICS.map((t) => (
                <option key={t} value={t}>
                  {TOPIC_LABEL[t]}
                  {summary.data
                    ? ` (${summary.data.byTopic.find((b) => b.topic === t)?.gold ?? 0}g)`
                    : ""}
                </option>
              ))}
            </select>
          ))}
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-4">
          <button
            onClick={onFetch}
            disabled={loading}
            className="btn-base"
          >
            {loading ? "Henter…" : "Hent 25 kandidater (~25 Firecrawl-units)"}
          </button>
          {status && <p className="meta italic">{status}</p>}
        </div>
        {errors.length > 0 && (
          <ul className="mt-3 text-[13px] text-bordeaux font-mono list-disc pl-5 space-y-1">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        )}
      </section>

      {candidates.length > 0 && (
        <>
          {(Object.keys(byTopic) as Topic[]).map((topic) => (
            <section key={topic} className="py-8 border-b border-rule">
              <h2 className="font-serif text-2xl mb-4">
                {TOPIC_LABEL[topic]}{" "}
                <span className="meta italic font-normal">
                  · {byTopic[topic].length} kandidater
                </span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {byTopic[topic].map((c) => {
                  const v = verdicts[c.url];
                  return (
                    <article
                      key={c.url}
                      className={`border p-4 flex flex-col gap-3 transition-colors ${
                        v === "gold"
                          ? "border-green-700 bg-green-50/40"
                          : v === "anti"
                          ? "border-bordeaux bg-bordeaux/5"
                          : "border-rule"
                      }`}
                    >
                      <div>
                        <p className="eyebrow mb-1">{c.source}</p>
                        <h3 className="font-serif text-[18px] leading-tight m-0">
                          <a
                            href={c.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-bordeaux"
                          >
                            {c.title}
                          </a>
                        </h3>
                      </div>
                      <p className="font-serif italic text-[14px] leading-[1.55] text-ink-body m-0">
                        {c.preview}…
                      </p>
                      <div className="flex flex-wrap gap-2 mt-auto">
                        <button
                          onClick={() => setVerdicts((p) => ({ ...p, [c.url]: "gold" }))}
                          className={`eyebrow px-3 py-1 border ${
                            v === "gold"
                              ? "bg-green-700 text-paper border-green-700"
                              : "border-rule hover:border-green-700 hover:text-green-700"
                          }`}
                        >
                          Guld
                        </button>
                        <button
                          onClick={() => setVerdicts((p) => ({ ...p, [c.url]: "anti" }))}
                          className={`eyebrow px-3 py-1 border ${
                            v === "anti"
                              ? "bg-bordeaux text-paper border-bordeaux"
                              : "border-rule hover:border-bordeaux hover:text-bordeaux"
                          }`}
                        >
                          Afvis
                        </button>
                        <button
                          onClick={() => setVerdicts((p) => ({ ...p, [c.url]: "skip" }))}
                          className={`eyebrow px-3 py-1 border ${
                            v === "skip"
                              ? "bg-ink-soft text-paper border-ink-soft"
                              : "border-rule hover:border-ink-soft"
                          }`}
                        >
                          Spring over
                        </button>
                        <a
                          href={c.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="eyebrow px-3 py-1 border border-rule hover:text-bordeaux ml-auto"
                        >
                          Åbn ↗
                        </a>
                      </div>
                      {(v === "gold" || v === "anti") && (
                        <input
                          type="text"
                          placeholder="Kort note til dig selv (valgfri) – fx 'perfekt tone' eller 'for generisk'"
                          value={reasons[c.url] ?? ""}
                          onChange={(e) =>
                            setReasons((p) => ({ ...p, [c.url]: e.target.value }))
                          }
                          className="border border-rule bg-paper px-3 py-1.5 font-serif text-[13px] italic"
                          maxLength={400}
                        />
                      )}
                    </article>
                  );
                })}
              </div>
            </section>
          ))}

          <div className="sticky bottom-4 mt-8 flex flex-wrap items-center gap-4 bg-paper border border-rule shadow-lg p-4">
            <p className="font-serif text-[15px]">
              <strong>{goldCount}</strong> markeret som guld ·{" "}
              <strong>{antiCount}</strong> som afvist ·{" "}
              <span className="meta italic">
                {candidates.length - goldCount - antiCount - Object.values(verdicts).filter(v => v === "skip").length} uden valg
              </span>
            </p>
            <button
              onClick={onSave}
              disabled={saving || goldCount + antiCount === 0}
              className="btn-base ml-auto"
            >
              {saving ? "Gemmer…" : `Gem ${goldCount + antiCount} valg`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
